import type { RoutingRule } from "@p0security/shared/types/workflow/types";
import { useLoadRoutingRules } from "components/Jit/Routing/useLoadRoutingRules";
import { hashRule } from "components/Jit/Routing/utils";
import { useMemo } from "react";
import { useRoutingRulesStore } from "store/routingRulesStore";

import type { PolicySummary } from "../graph/types.ts";

/**
 * Returns the tenant's routing rules that are specifically targeted at
 * agentic requestors — the real P0 access policies that govern AI agents.
 *
 * Reads from the same `useRoutingRulesStore` that Policy Studio owns, and
 * triggers a load of `routing/latest` into that store if it's empty. This
 * avoids the prior pattern of running our own parallel fetch, which (a)
 * doubled network traffic and (b) could race the Policy Studio loader.
 */
export const useAgenticPolicies = (): {
  policies: PolicySummary[];
  loading: boolean;
} => {
  useLoadRoutingRules();
  const jsonRules = useRoutingRulesStore((s) => s.jsonRules) as RoutingRule[];
  const loading = useRoutingRulesStore((s) => s.isLoading);

  const policies = useMemo<PolicySummary[]>(() => {
    return jsonRules
      .filter((rule) => rule.requestor?.type === "agentic")
      .map((rule, idx) => ({
        id: hashRule(rule),
        name: rule.name ?? `Unnamed agentic rule ${idx + 1}`,
        enabled: rule.disabled !== true,
      }));
  }, [jsonRules]);

  return { policies, loading };
};
